import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSecureConfig } from '../../hooks/useSecureConfig';

const SecureConfigContext = createContext();

/**
 * Provider component to manage secure configuration across the application
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.userIdentifier - User identifier for encryption
 * @returns {React.Component} - Provider component
 */
export const SecureConfigProvider = ({ children, userIdentifier }) => {
    const { config, loading, error, refetch, clearConfig } = useSecureConfig(userIdentifier, true);
    const [isConfigReady, setIsConfigReady] = useState(false);

    useEffect(() => {
        if (config && !loading && !error) {
            setIsConfigReady(true);

            // Set up global configuration access
            if (typeof window !== 'undefined') {
                // Make config available globally (but not in window for security)
                window.getSecureConfig = () => config;
            }
        } else {
            setIsConfigReady(false);

            // Clean up global config access when not ready
            if (typeof window !== 'undefined') {
                delete window.getSecureConfig;
            }
        }
    }, [config, loading, error]);

    const value = {
        config,
        loading,
        error,
        isConfigReady,
        refetch,
        clearConfig,
        // Helper functions to access specific config values with fallbacks
        getAuthDomain: () => config?.REACT_APP_AUTH_DOMAIN || null,
        getAuthClient: () => config?.REACT_APP_AUTH_CLIENT || null,
        getAppToken: () => config?.REACT_APP_TOKEN || null,
        getAppClient: () => config?.REACT_APP_CLIENT || null,
        getAppKey: () => config?.REACT_APP_KEY || config?.app_key || null,
        getAppSalt: () => config?.app_salt || null,
        // Google Login client ID
        getGoogleClientId: () => config?.REACT_APP_CLIENT || null,
        // Check if we have a working secure configuration
        hasSecureConfig: () => isConfigReady && !error,
        // Auto-retry helper for API calls
        ensureConfigOrRetry: async () => {
            if (isConfigReady) return true;
            if (!loading) {
                refetch();
            }
            return false;
        },
    };

    return <SecureConfigContext.Provider value={value}>{children}</SecureConfigContext.Provider>;
};

/**
 * Hook to access secure configuration
 * @returns {object} - Configuration context value
 */
export const useSecureConfigContext = () => {
    const context = useContext(SecureConfigContext);
    if (!context) {
        throw new Error('useSecureConfigContext must be used within a SecureConfigProvider');
    }
    return context;
};

/**
 * HOC to ensure component only renders when secure config is ready
 * @param {React.Component} WrappedComponent - Component to wrap
 * @returns {React.Component} - Enhanced component
 */
export const withSecureConfig = (WrappedComponent) => {
    return function SecureConfigWrapper(props) {
        const { isConfigReady, loading, error, refetch } = useSecureConfigContext();

        // Don't block rendering - always render the wrapped component
        // This allows fallback values to be used in components
        return <WrappedComponent {...props} />;
    };
};

export default SecureConfigContext;
